<template>
  <div :class="[formErrorClass(name), 'form-group', floating && 'form-group--floating']">
    <!-- floating mode: slot first so CSS sibling selector (~ label) works -->
    <template v-if="floating">
      <slot v-bind="{ id }"/>
      <label v-if="label" ref="label" :for="id" class="col-form-label" v-text="label"/>
    </template>
    <!-- default mode: label then slot (original behaviour) -->
    <template v-else>
      <label v-if="label" ref="label" :for="id" class="col-form-label" v-text="label"/>
      <slot v-bind="{ id }"/>
    </template>
    <ErrorText v-for="_name in names" :key="_name" :errorContainer="errorContainer" :name="_name"/>
  </div>
</template>

<script>
import ErrorText from '@modules/shared/components/form/ErrorText';
import ServerErrorMixin from '@modules/shared/mixins/ServerErrorMixin';
import {Str} from '@/utilities/random';

const isInputElement = (el) => {
  if (!(el instanceof HTMLElement)) return false;
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLSelectElement ||
    el instanceof HTMLTextAreaElement ||
    el.isContentEditable
  );
};

export default {
  name: 'FormGroup',
  mixins: [ServerErrorMixin],
  components: {ErrorText},
  props: {
    flex: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
    name: {
      required: false,
      default: () => [],
    },
    errorContainer: {
      type: String,
      required: false,
      default: '',
    },
    /** When true the label sits after the input enabling CSS floating-label animation */
    floating: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    names() {
      if (Array.isArray(this.name)) return this.name;
      return [this.name];
    },
    id: () => Str.random({length: 6, unique: true}),
  },
  watch: {
    errorContainer: {
      handler(value) {
        if (value) this.registerErrorContainer(value);
      },
      immediate: true,
    },
  },
  mounted() {
    // Non-floating: wire the label to the first child of the slot.
    if (!this.floating) {
      const nextElement = this.$refs.label?.nextElementSibling ?? null;
      if (this.label && nextElement) {
        if (isInputElement(nextElement)) {
          nextElement.id = this.id;
        } else {
          // Custom control (e.g. RadioButtonGroup, LoanPurposePicker) — a native
          // `for`/`id` pairing only works when it points at a real form control,
          // so link the label to the control's root via aria-labelledby instead.
          this.$refs.label.id = this.id;
          nextElement.setAttribute('aria-labelledby', this.id);
        }
      }
      return;
    }

    // Floating: wire label.for to the first input child — but prefer a
    // visible custom control (e.g. FmSelect's combobox) over a same-purpose
    // hidden native input when both exist, so the accessible name lands on
    // the element assistive tech and keyboard users actually interact with.
    const inputs = this.$el.querySelectorAll('input, select, textarea');
    const visibleControl = this.$el.querySelector('[role="combobox"], [role="radiogroup"]');
    if (this.$refs.label) {
      if (visibleControl) {
        this.$refs.label.id = this.id;
        visibleControl.setAttribute('aria-labelledby', this.id);
      } else if (inputs.length) {
        inputs[0].id = this.id;
      }
    }

    // Track whether each input has a value so we can keep the label floated
    const updateHasValue = () => {
      const hasVal = Array.from(inputs).some(el => {
        if (el instanceof HTMLSelectElement) {
          // selects always have a selected option — treat as "has value"
          // if it isn't the blank/disabled placeholder
          return el.value !== '' && el.value !== null;
        }
        return el.value !== '';
      });
      this.$el.classList.toggle('fm-has-value', hasVal);
    };

    inputs.forEach(input => {
      input.addEventListener('input', updateHasValue);
      input.addEventListener('change', updateHasValue);
    });

    // $nextTick ensures Vue has finished flushing all reactive values to the DOM
    // before we check — critical for selects (v-for options) and store-prefilled fields.
    this.$nextTick(updateHasValue);
  },
};
</script>

<style>
.form-group.flex {
  display: flex;
  flex-direction: column;
}

.has-error .form-control {
  border-color: #e53e3e !important;
  box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.12) !important;
}

/* ── Floating label ─────────────────────────────────────── */
.form-group--floating {
  position: relative;
  padding-top: 0;
  margin-bottom: 1.1rem;
}

.form-group--floating .form-control {
  padding: 1.55rem 0.85rem 0.4rem; /* top raised so label never overlaps value */
  height: auto;
  min-height: 56px;
  border-radius: 12px;
  border: 1.5px solid #E7E4DA;
  font-size: 15px;
  color: #15171C;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}

/* Select needs explicit height since it ignores padding-top in some browsers */
.form-group--floating select.form-control {
  height: 56px;
  padding-top: 1.4rem;
  padding-bottom: 0.2rem;
}

.form-group--floating .form-control::placeholder {
  color: transparent;
}

.form-group--floating .form-control:focus {
  border-color: #FFB000;
  box-shadow: 0 0 0 3px rgba(255, 176, 0, 0.12);
  outline: none;
}

.form-group--floating .col-form-label {
  position: absolute;
  top: 1.05rem;
  left: 0.88rem;
  right: 0.5rem;
  /* Reset Bootstrap's col-form-label padding — it shifts the label down into
     the input value text when floating. Must be 0 so top/font-size transitions
     are pixel-accurate. */
  padding-top: 0;
  padding-bottom: 0;
  margin-bottom: 0;
  font-size: 14.5px;
  font-weight: 400;
  line-height: 1;
  /* WCAG 1.4.3: was #9197A1 (~2.94:1 on white, fails the 4.5:1 minimum for
     normal text). #6B7280 clears AA on white/#FBFAF6/#F4F2EC/#F0EEE7. */
  color: #6B7280;
  pointer-events: none;
  /* Bootstrap bumps a focused .input-group > .form-control to z-index: 3
     (input-group.css, so its border/shadow shows above adjacent prepend/append
     boxes). Fields wrapped in .input-group (e.g. the "$" prepend) inherit that
     rule, so on focus the input's own opaque background was painting above
     this absolutely-positioned label instead of below it — the label would
     vanish the moment the field was focused. z-index here just needs to clear
     that 3. */
  z-index: 4;
  transition: top 0.15s ease, font-size 0.15s ease, color 0.15s ease, left 0.15s ease, font-weight 0.15s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Fields wrapped in an input-group prepend (e.g. the "$" box on amount/income/
   expense fields) — the label's default `left` assumes the input starts right
   at the field's edge. With a prepend present, that same `left` lands the
   label underneath the prepend's opaque background/border instead, hiding it
   both at rest and while floated/focused. Shift it clear of the prepend. */
.form-group--floating .input-group ~ .col-form-label {
  left: 3rem;
}

/* Label floats up when input is focused. WCAG 1.4.3: was #FFB000 (~1.83:1 on
   white, fails badly). #5B606B matches the already-AA-safe "has value" color
   below (~6:1 on white) so focused and filled states read the same way. */
.form-group--floating:focus-within .col-form-label {
  top: 0.3rem;  /* sits at top of input with a small gap */
  font-size: 10.5px;
  font-weight: 600;
  color: #5B606B;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* Label stays floated when input has a value (JS-driven via fm-has-value) */
.form-group--floating.fm-has-value .col-form-label {
  top: 0.3rem;
  font-size: 10.5px;
  font-weight: 600;
  color: #5B606B;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* CSS-only fallback: float label for readonly, pre-populated, or autocomplete-filled inputs.
   Works whenever the placeholder is not shown (i.e. the input has a value), even when no
   input/change DOM event fires (programmatic :value binding, Google Places fill, etc.) */
.form-group--floating .form-control:not(:placeholder-shown) ~ .col-form-label {
  top: 0.3rem;
  font-size: 10.5px;
  font-weight: 600;
  color: #5B606B;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* Same CSS-only fallback, for fields whose .form-control sits inside an
   input-group prepend wrapper — the sibling combinator above can't reach
   through that extra nesting level, so it needs :has() instead. */
.form-group--floating:has(.input-group .form-control:not(:placeholder-shown)) .col-form-label {
  top: 0.3rem;
  font-size: 10.5px;
  font-weight: 600;
  color: #5B606B;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* Focus overrides the muted color from fm-has-value — kept the same AA-safe
   color as the non-focused floated state (see note above). */
.form-group--floating.fm-has-value:focus-within .col-form-label {
  color: #5B606B;
}

/* Focus override for the CSS-only path */
.form-group--floating:focus-within .form-control:not(:placeholder-shown) ~ .col-form-label {
  color: #5B606B;
}

/* Focus override for the CSS-only path — input-group-wrapped variant */
.form-group--floating:focus-within:has(.input-group .form-control:not(:placeholder-shown)) .col-form-label {
  color: #5B606B;
}

/* Textarea: give extra height */
.form-group--floating textarea.form-control {
  padding-top: 1.6rem;
  min-height: 100px;
  resize: vertical;
}

/* Input-group inside floating: wrapper keeps the float context */
.form-group--floating .input-group {
  position: relative;
}

.form-group--floating .input-group .form-control {
  /* border-radius handled per-side by Bootstrap input-group, override */
  border-radius: 12px 0 0 12px;
}

.form-group--floating .input-group .input-group-text {
  background: #F9F8F5;
  border: 1.5px solid #E7E4DA;
  border-left: none;
  border-radius: 0 12px 12px 0;
  color: #5B606B;
  font-size: 14px;
}

/* Prepend variant */
.form-group--floating .input-group .input-group-prepend .input-group-text {
  border-radius: 12px 0 0 12px;
  border-left: 1.5px solid #E7E4DA;
  border-right: none;
}

.form-group--floating .input-group .input-group-prepend ~ .form-control {
  border-radius: 0 12px 12px 0;
  border-left: none;
}

/* Error state */
.form-group--floating.has-error .form-control {
  border-color: #e53e3e !important;
  box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.1) !important;
}

.form-group--floating.has-error .col-form-label {
  color: #e53e3e;
}
</style>
